import { api } from "./baseApi";
import { ApiResponse, PaginationMeta } from "@/types";

export interface SalesReportData {
  summary: {
    totalTransactions: number;
    totalReturns: number;
    grossSales: number;
    totalReturned: number;
    netSales: number;
    totalDiscount: number;
    totalTax: number;
    totalProfit: number;
    profitMargin: number;
  };
  salesByPeriod: {
    period: string;
    transactions: number;
    sales: number;
    returns: number;
    net: number;
    profit: number;
  }[];
  topProducts: {
    item: {
      id: string;
      name: string;
      sku?: string;
      category: { name: string };
    };
    quantity: number;
    revenue: number;
    profit: number;
    profitMargin: number;
  }[];
  paymentMethods: { [key: string]: number };
}

export interface InventoryReportData {
  summary: {
    totalProducts: number;
    totalQuantity: number;
    totalValue: number;
    outOfStock: number;
    lowStock: number;
    overstocked: number;
  };
  byCategory: {
    category: string;
    products: number;
    quantity: number;
    value: number;
  }[];
  topMovers: {
    item: {
      id: string;
      name: string;
      sku?: string;
      category?: { name: string };
      baseUnit?: string;
    };
    added: number;
    sold: number;
    adjusted: number;
    damaged: number;
    returned: number;
    totalMovement: number;
    revenue?: number;
    currentStock: number;
    averageDailySales: number;
    stockCoverageDays: number | null;
    periodDays: number;
    unitBreakdown?: {
      unitName: string;
      quantity: number;
      baseQuantity: number;
      conversionFactor: number | null;
    }[];
    unitConversions?: {
      unitName: string;
      conversionFactor: number;
      isBaseUnit: boolean;
    }[];
  }[];
  reorder: {
    suggestions: {
      item: {
        id: string;
        name: string;
        sku?: string;
        category?: { name: string };
      };
      currentStock: number;
      lowStockThreshold: number;
      averageDailyDemand: number;
      daysUntilStockout: number | null;
      projectedStockoutDate: string | null;
      recommendedOrderQuantity: number;
      recommendedReorderDate: string;
    }[];
    settings: {
      analysisDays: number;
      leadTimeDays: number;
      coverageDays: number;
    };
  };
  criticalStock: {
    data: {
      item: {
        id: string;
        name: string;
        sku?: string;
        category: { name: string };
      };
      currentStock: number;
      lowStockThreshold: number;
      shortage: number;
    }[];
    pagination: PaginationMeta;
  };
}

export interface CustomerReportData {
  summary: {
    totalCustomers: number;
    newCustomers: number;
    repeatCustomers: number;
    repeatRate: number;
  };
  topCustomers: {
    customerId: string;
    customerName: string;
    email: string | null;
    phone: string | null;
    totalTransactions: number;
    totalSpent: number;
    averageTransaction: number;
    lastPurchase: string;
    loyaltyPoints: number;
  }[];
}

export interface FinancialReportData {
  revenue: {
    totalRevenue: number;
    totalReturns: number;
    netRevenue: number;
    transactionCount: number;
    totalQuantitySold: number;
    averageTransactionValue: number;
  };
  costs: {
    totalCOGS: number;
    totalExpenses: number;
    totalCosts: number;
  };
  profit: {
    grossProfit: number;
    netProfit: number;
    grossMargin: number;
    netMargin: number;
  };
  expensesByCategory: {
    category: string;
    amount: number;
    percentage: number;
  }[];
  cashFlow: {
    date: string;
    inflows: number;
    outflows: number;
    netCashFlow: number;
  }[];
}

export interface ReportFilters {
  startDate?: string;
  endDate?: string;
  categoryId?: string;
  customerId?: string;
  productId?: string;
  paymentMethod?: string;
  period?: "daily" | "weekly" | "monthly";
  groupBy?: "day" | "week" | "month";
  includeInactive?: boolean;
  limit?: number;
  criticalPage?: number;
  criticalLimit?: number;
  movementRange?: "week" | "month";
  movementCategoryId?: string;
  movementStartDate?: string;
  movementEndDate?: string;
  movementLimit?: number;
  movementSearch?: string;
  reorderLeadTime?: number;
  reorderCoverage?: number;
  outletId?: string;
  storeId?: string;
}

export interface ExportReportParams {
  type: "sales" | "inventory" | "customer" | "financial";
  format: "excel" | "csv" | "pdf";
  startDate?: string;
  endDate?: string;
  [key: string]: any;
}

// Multi-outlet comparison types
export interface OutletPerformanceMetrics {
  outletId: string;
  outletName: string;
  sales: number;
  transactions: number;
  profit: number;
  expenses: number;
  inventoryValue: number;
  customerCount: number;
  averageTransaction: number;
  profitMargin: number;
  growthRate?: number;
}

export interface OutletComparisonMetrics {
  metric: string;
  outlets: {
    outletId: string;
    outletName: string;
    value: number;
    percentage: number;
    rank: number;
  }[];
}

export interface OutletComparisonFilters {
  startDate: string;
  endDate: string;
  outletIds?: string[];
  storeId?: string;
  metrics?: string[];
}

export interface OutletRankingFilters {
  startDate: string;
  endDate: string;
  storeId: string;
  rankBy?: "sales" | "profit" | "transactions" | "profit_margin";
  limit?: number;
}

export interface OutletAnalysisFilters {
  startDate: string;
  endDate: string;
  outletIds?: string[];
  storeId?: string;
  analysisType?: "sales_distribution" | "performance_gaps" | "trend_correlation";
}

export interface SalesDistributionData {
  totalSales: number;
  distribution: {
    outletId: string;
    outletName: string;
    sales: number;
    percentage: number;
    transactions: number;
    averageTransaction: number;
  }[];
}

export interface PerformanceGapsData {
  averages: {
    sales: number;
    profit: number;
    transactions: number;
    profitMargin: number;
  };
  topPerformer: {
    bySales: OutletPerformanceMetrics;
    byProfit: OutletPerformanceMetrics;
  };
  bottomPerformer: {
    bySales: OutletPerformanceMetrics;
    byProfit: OutletPerformanceMetrics;
  };
  gaps: {
    outletId: string;
    outletName: string;
    salesGap: number;
    salesGapPercentage: number;
    profitGap: number;
    profitGapPercentage: number;
    transactionsGap: number;
    transactionsGapPercentage: number;
  }[];
}

export interface TrendCorrelationData {
  outlets: {
    outletId: string;
    outletName: string;
    trend: {
      period: string;
      sales: number;
      count: number;
    }[];
  }[];
  groupBy: "day" | "week" | "month";
}

export interface MultiOutletDashboardData {
  summary: {
    totalSales: number;
    totalTransactions: number;
    totalProfit: number;
    totalExpenses: number;
    totalInventoryValue: number;
    totalCustomerCount: number;
    averageTransaction: number;
    overallProfitMargin: number;
    outletCount: number;
  };
  outlets: (Omit<OutletPerformanceMetrics, "growthRate"> & {
    growthRate: number | null;
    trend: number | null;
  })[];
}

export interface FinancialPeriodMetrics {
  totalRevenue: number;
  cogs: number;
  grossProfit: number;
  totalExpenses: number;
  netProfit: number;
  grossMargin: number;
  netMargin: number;
}

export interface StoreFinancialAnalysisData {
  summary: FinancialPeriodMetrics;
  comparison: FinancialPeriodMetrics;
  revenueByOutlet: { name: string; value: number }[];
  expenseByOutlet: { name: string; value: number }[];
  expenseByCategory: { name: string; value: number }[];
  outletContribution: {
    outlet: string;
    revenue: number;
    cogs: number;
    profit: number;
    contribution: number;
  }[];
  revenueTrend: { period: string; revenue: number; expenses: number; profit: number }[];
  revenueVsExpenses: { period: string; revenue: number; expenses: number }[];
}

export interface NearExpiryItem {
  batchId: string;
  batchCode?: string;
  expiryDate: string;
  daysToExpiry: number;
  remainingQuantity: number;
  outletId: string;
  outletName: string | null;
  item: {
    id: string;
    name: string;
    sku?: string;
    baseUnit?: string;
    category: string | null;
  };
}

export interface StockAuditReportData {
  summary: {
    totalAudits: number;
    approvedAudits: number;
    activeAudits: number;
    totalItemsAdjusted: number;
    totalCounted: number;
    accuracyRate: number | null;
  };
  recentAudits: {
    id: string;
    auditCode: string;
    type: string;
    status: string;
    outlet: { name: string };
    creator: { name: string };
    createdAt: string;
    summary: { total: number; counted: number; withDiscrepancy: number } | null;
  }[];
  topDiscrepancyItems: {
    itemId: string;
    item: { name: string; sku: string };
    totalDiscrepancy: number;
    auditCount: number;
  }[];
}

export interface StockAuditReportFilters {
  startDate?: string;
  endDate?: string;
  outletId?: string;
  storeId?: string;
}

export interface NearExpiryParams {
  days?: number;
  outletId?: string;
  storeId?: string;
  page?: number;
  limit?: number;
}

export interface NearExpiryResponse {
  data: NearExpiryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  thresholdDays: number;
}

export const reportApi = api.injectEndpoints({
  endpoints: builder => ({
    // Sales Reports
    getSalesReport: builder.query<ApiResponse<SalesReportData>, ReportFilters>({
      query: filters => ({
        url: "/reports/sales",
        params: filters,
      }),
      providesTags: ["Report"],
    }),

    // Inventory Reports
    getInventoryReport: builder.query<ApiResponse<InventoryReportData>, ReportFilters>({
      query: filters => ({
        url: "/reports/inventory",
        params: filters,
      }),
      providesTags: ["Report"],
    }),

    // Customer Reports
    getCustomerReport: builder.query<ApiResponse<CustomerReportData>, ReportFilters>({
      query: filters => ({
        url: "/reports/customers",
        params: filters,
      }),
      providesTags: ["Report"],
    }),

    // Financial Reports
    getFinancialReport: builder.query<ApiResponse<FinancialReportData>, ReportFilters>({
      query: filters => ({
        url: "/reports/financial",
        params: filters,
      }),
      providesTags: ["Report"],
    }),

    getStoreFinancialAnalysis: builder.query<ApiResponse<StoreFinancialAnalysisData>, { startDate: string; endDate: string; comparison?: string; storeId: string }>({
      query: params => ({
        url: "/reports/financial/store-analysis",
        params,
      }),
      providesTags: ["Report"],
    }),

    // Export Report
    exportReport: builder.mutation<Blob, ExportReportParams>({
      query: params => ({
        url: "/reports/export",
        method: "GET",
        params,
        // Successful responses are returned as a Blob; failures are parsed as JSON so the
        // backend error payload is available in `error.data`.
        responseHandler: async (response: Response) =>
          response.ok ? response.blob() : response.json(),
      }),
    }),

    // Multi-outlet Comparison Reports
    getMultiOutletDashboard: builder.query<ApiResponse<MultiOutletDashboardData>, { startDate: string; endDate: string; storeId: string }>({
      query: params => ({
        url: "/reports/multi-outlet/dashboard",
        params,
      }),
      providesTags: ["Report"],
    }),

    compareOutlets: builder.query<ApiResponse<OutletComparisonMetrics[]>, OutletComparisonFilters>({
      query: filters => ({
        url: "/reports/multi-outlet/compare",
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate,
          ...(filters.outletIds && { outletIds: filters.outletIds.join(",") }),
          ...(filters.storeId && { storeId: filters.storeId }),
          ...(filters.metrics && { metrics: filters.metrics.join(",") }),
        },
      }),
      providesTags: ["Report"],
    }),

    rankOutlets: builder.query<ApiResponse<OutletPerformanceMetrics[]>, OutletRankingFilters>({
      query: filters => ({
        url: "/reports/multi-outlet/ranking",
        params: filters,
      }),
      providesTags: ["Report"],
    }),

    analyzeOutlets: builder.query<
      ApiResponse<SalesDistributionData | PerformanceGapsData | TrendCorrelationData>,
      OutletAnalysisFilters
    >({
      query: filters => ({
        url: "/reports/multi-outlet/analysis",
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate,
          ...(filters.outletIds && { outletIds: filters.outletIds.join(",") }),
          ...(filters.storeId && { storeId: filters.storeId }),
          ...(filters.analysisType && { analysisType: filters.analysisType }),
        },
      }),
      providesTags: ["Report"],
    }),

    getNearExpiryItems: builder.query<ApiResponse<NearExpiryResponse>, NearExpiryParams>({
      query: (params) => ({
        url: "/reports/inventory/near-expiry",
        params,
      }),
      providesTags: ["Report"],
    }),

    getStockAuditReport: builder.query<ApiResponse<StockAuditReportData>, StockAuditReportFilters>({
      query: (params) => ({
        url: "/reports/stock-audit",
        params,
      }),
      providesTags: ["Report"],
    }),
  }),
});

export const {
  useGetSalesReportQuery,
  useGetInventoryReportQuery,
  useGetCustomerReportQuery,
  useGetFinancialReportQuery,
  useExportReportMutation,
  useGetMultiOutletDashboardQuery,
  useCompareOutletsQuery,
  useRankOutletsQuery,
  useAnalyzeOutletsQuery,
  useGetStoreFinancialAnalysisQuery,
  useGetNearExpiryItemsQuery,
  useGetStockAuditReportQuery,
} = reportApi;
